import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Gift, Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppSumoRedeem = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);
  
  const [newAccountData, setNewAccountData] = useState({
    license_code: searchParams.get('code') || '',
    email: '',
    password: '',
    organization_name: ''
  });
  
  const [existingAccountData, setExistingAccountData] = useState({
    license_code: searchParams.get('code') || ''
  });

  const tierBenefits = {
    1: {
      name: 'Tier 1',
      limit: '500 certificates/year',
      price: '$69',
      features: ['500 certificates per year', 'All templates', 'PDF & PNG downloads', 'Email delivery', 'LinkedIn integration', 'Public verification']
    },
    2: {
      name: 'Tier 2',
      limit: '2,000 certificates/year',
      price: '$139',
      features: ['2,000 certificates per year', 'All templates', 'PDF & PNG downloads', 'Email delivery', 'LinkedIn integration', 'Public verification', 'Priority support']
    },
    3: {
      name: 'Tier 3',
      limit: 'Unlimited',
      price: '$209',
      features: ['Unlimited certificates', 'All templates', 'PDF & PNG downloads', 'Email delivery', 'LinkedIn integration', 'Public verification', 'Priority support', 'Custom branding']
    }
  };

  const handleNewAccountRedeem = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/appsumo/redeem`, newAccountData);
      setTierInfo(response.data);
      setSuccess(true);
      toast.success('Account created and license activated!');
    } catch (err) {
      console.error('Redeem error:', err);
      
      let errorMessage = 'Failed to redeem license code';
      const detail = err.response?.data?.detail;
      
      if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || 'Validation error').join('. ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.message) {
        errorMessage = detail.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleExistingAccountRedeem = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('issuer_token');
      if (!token) {
        toast.error('Please login first');
        navigate('/issuer/login?redirect=/appsumo');
        return;
      }
      
      // Get issuer data to pass issuer_id
      const issuerData = JSON.parse(localStorage.getItem('issuer_data') || '{}');
      
      const response = await axios.post(
        `${API}/appsumo/redeem-existing`,
        existingAccountData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Issuer-ID': issuerData.id
          }
        }
      );
      
      setTierInfo(response.data);
      setSuccess(true);
      toast.success('License code added to your account!');
    } catch (err) {
      console.error('Redeem error:', err);
      
      let errorMessage = 'Failed to redeem license code';
      const detail = err.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.message) {
        errorMessage = detail.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success && tierInfo) {
    const tier = tierInfo.new_tier || tierInfo.tier;
    const benefits = tierBenefits[tier];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full mb-4">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome to {benefits.name}!</h1>
            <p className="text-slate-600">Your license has been activated successfully</p>
          </div>
          
          <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Your Plan Includes:</h2>
            <div className="space-y-3">
              {benefits.features.map((feature, idx) => (
                <div key={idx} className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/issuer/dashboard')}
              className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg py-3"
            >
              Go to Dashboard
            </Button>
            {tierInfo.previous_tier ? (
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50 rounded-lg py-3"
              >
                Redeem Another Code
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/issuer/login')}
                variant="outline"
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50 rounded-lg py-3"
              >
                Login to Account
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full mb-4">
            <Gift className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Redeem Your AppSumo Code</h1>
          <p className="text-slate-600 text-lg">Unlock your lifetime certificate platform</p>
        </div>

        {/* Tier Comparison */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map(tier => {
            const benefits = tierBenefits[tier];
            return (
              <div key={tier} className={`bg-white rounded-xl p-6 border-2 ${
                tier === 3 ? 'border-amber-500 shadow-lg' : 'border-slate-200'
              }`}>
                {tier === 3 && (
                  <div className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold text-slate-900 mb-1">{benefits.name}</h3>
                <p className="text-2xl font-bold text-amber-600 mb-1">{benefits.price}</p>
                <p className="text-slate-600 text-sm mb-4">{benefits.limit}</p>
                <ul className="space-y-2">
                  {benefits.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm text-slate-600">
                      <Check className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Redemption Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <Tabs defaultValue="new" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="new">New Account</TabsTrigger>
              <TabsTrigger value="existing">Existing Account</TabsTrigger>
            </TabsList>
            
            <TabsContent value="new">
              <form onSubmit={handleNewAccountRedeem} className="space-y-6">
                <div>
                  <Label htmlFor="new-code" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    AppSumo License Code
                  </Label>
                  <Input
                    id="new-code"
                    value={newAccountData.license_code}
                    onChange={(e) => setNewAccountData({...newAccountData, license_code: e.target.value.toUpperCase()})}
                    placeholder="APPSUMO-XXX-TIER-X"
                    required
                    className="h-11 rounded-lg border-amber-200 focus:border-amber-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Find this in your AppSumo account</p>
                </div>
                
                <div>
                  <Label htmlFor="new-org" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Organization Name
                  </Label>
                  <Input
                    id="new-org"
                    value={newAccountData.organization_name}
                    onChange={(e) => setNewAccountData({...newAccountData, organization_name: e.target.value})}
                    placeholder="Your Company Name"
                    required
                    className="h-11 rounded-lg border-amber-200 focus:border-amber-500"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Email Address
                  </Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newAccountData.email}
                    onChange={(e) => setNewAccountData({...newAccountData, email: e.target.value})}
                    placeholder="you@example.com"
                    required
                    className="h-11 rounded-lg border-amber-200 focus:border-amber-500"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-password" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Password
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newAccountData.password}
                    onChange={(e) => setNewAccountData({...newAccountData, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    className="h-11 rounded-lg border-amber-200 focus:border-amber-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Must be at least 8 characters with uppercase letter and number</p>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg py-3 text-lg font-semibold"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Redeem Code & Create Account'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="existing">
              <form onSubmit={handleExistingAccountRedeem} className="space-y-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-900">
                    <strong>Note:</strong> You must be logged in to add a code to your existing account. Each code stacks to upgrade your tier!
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="existing-code" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    AppSumo License Code
                  </Label>
                  <Input
                    id="existing-code"
                    value={existingAccountData.license_code}
                    onChange={(e) => setExistingAccountData({license_code: e.target.value.toUpperCase()})}
                    placeholder="APPSUMO-XXX-TIER-X"
                    required
                    className="h-11 rounded-lg border-amber-200 focus:border-amber-500"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg py-3 text-lg font-semibold"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Add Code to My Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Help Text */}
        <div className="text-center mt-8">
          <p className="text-slate-600">
            Need help? Contact support at <a href="mailto:cs@instructing.com" className="text-amber-600 hover:text-amber-700 font-medium">cs@instructing.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppSumoRedeem;
